import type { Dimensions } from '@festina-life/core'
import { Alert } from '@festina-life/ui/feedback'
import { Checkbox } from '@festina-life/ui/forms'
import { useLazySvgTextSize, useTranslate } from '@festina-life/ui/hooks'
import { Group } from '@visx/group'
import { Polygon } from '@visx/shape'
import { type CSSProperties, Fragment, useMemo, useState } from 'react'

import { useLayoutContext } from '../../context/layoutContext'
import { useTemplateContext } from '../../context/templateContext'
import { useTemplateId } from '../../hooks/useTemplateId'
import { useTranslateLayer } from '../../hooks/useTranslateLayer'
import { useTemplateNavigation } from '../../system/useTemplateNavigation'
import type { TemplateVersionInfo } from '../../types/templateModel'
import { useTemplateHierarchy } from '../../uiModels/versionInfo/useTemplateHierarchy'
import {
  INHERITANCE_ARROW_RADIUS,
  NODE_SPACING_VERTICAL,
  TEXT_BOX_PADDING_X,
} from './constants'
import { createLayout, positionLayout, resolveLayout } from './d3Helpers'
import { ExpandedReplacementPath } from './ExpandedReplacementPath'
import { InheritancePathHorizontal } from './InheritancePathHorizontal'
import { InheritanceTreeNode } from './InheritanceTreeNode'
import type { Node } from './types'

export function ExpandedInheritanceDiagram() {
  const navigate = useTemplateNavigation()
  const templateId = useTemplateId()
  const translate = useTranslate()
  const { templateVersion } = useTemplateContext()

  const {
    preferences: {
      templates: { isFlattened },
    },
  } = useLayoutContext()
  const translateLayer = useTranslateLayer()
  const [isShowingReplacements, setIsShowingReplacements] = useState(true)

  const { dimensions, svgRef: svgRef, didMount } = useLazySvgTextSize()

  const templateHierarchy = useTemplateHierarchy({
    forTemplate: templateVersion?.versionInfo,
    withReplacements: false,
  })

  const { nodes, boundingBox } = useMemo(() => {
    if (!didMount || !templateHierarchy.length) {
      return { nodes: [], boundingBox: { width: 0, height: 0 } }
    }

    const rootTemplate = templateHierarchy[0]

    const strings: string[] = []
    const visitExtractStrings = (template: TemplateVersionInfo) => {
      strings.push(template.identifier)
      template.children.forEach(visitExtractStrings)
    }
    visitExtractStrings(rootTemplate)

    const measuredStrings: Record<string, Dimensions> = {}
    dimensions(strings).forEach((size, index) => {
      measuredStrings[strings[index]] = size
    })

    const hierarchicalNode = createLayout({
      templateVersion: rootTemplate,
      size: (template) => measuredStrings[template.identifier],
    })
    const displayNode = positionLayout(hierarchicalNode)
    const nodes = resolveLayout(displayNode)

    return { nodes, boundingBox: displayNode.boundingBox }
  }, [didMount, dimensions, templateHierarchy])

  return (
    <div
      style={
        {
          '--diagram-height': `${boundingBox.height + NODE_SPACING_VERTICAL}px`,
          '--diagram-width': `${boundingBox.width}px`,
        } as CSSProperties
      }
    >
      <Checkbox
        checked={isShowingReplacements}
        onChange={() => setIsShowingReplacements(!isShowingReplacements)}
        wrapperProps={{
          className: 'py-0!',
          label: translate(
            'templateViewer.sidebar.inheritanceDiagram.showingReplacements.label',
          ),
        }}
      />
      <Alert
        severity="info"
        className="my-2"
        title={translate(
          'templateViewer.sidebar.inheritanceDiagram.layersWarning',
          { layer: translateLayer(templateVersion?.layer) },
        )}
      />
      <div className="overflow-auto w-(--diagram-width) h-(--diagram-height) flex items-center justify-center">
        <svg
          width={boundingBox.width}
          height={boundingBox.height + NODE_SPACING_VERTICAL}
        >
          <Group innerRef={svgRef} top={NODE_SPACING_VERTICAL}>
            {nodes.map((node) => {
              const replacedNode =
                node.replacesId &&
                nodes.find(
                  (searchedNode) => searchedNode.id === node.replacesId,
                )

              const leftX = (measuredNode: Node) => measuredNode.position.x
              const rightX = (measuredNode: Node) =>
                measuredNode.position.x + measuredNode.boundingBox.width

              const topY = (measuredNode: Node) => measuredNode.position.y
              const middleY = (measuredNode: Node) =>
                measuredNode.position.y + measuredNode.boundingBox.height / 2

              const replacesPathFrom = replacedNode
                ? {
                    x:
                      rightX(node) < leftX(replacedNode)
                        ? rightX(node) - 2 * TEXT_BOX_PADDING_X
                        : leftX(node) + TEXT_BOX_PADDING_X,
                    y: topY(node),
                  }
                : undefined

              const replacesPathTo =
                replacedNode && replacesPathFrom
                  ? {
                      x:
                        leftX(replacedNode) > leftX(node)
                          ? leftX(replacedNode)
                          : rightX(replacedNode),
                      y: middleY(replacedNode),
                    }
                  : undefined

              return (
                <Fragment key={node.text}>
                  <InheritanceTreeNode
                    x={node.position.x}
                    y={node.position.y}
                    text={node.text}
                    isActive={
                      isFlattened
                        ? templateHierarchy.some(
                            (template) => template.identifier === node.id,
                          )
                        : node.id === templateId
                    }
                    isCurrent={node.id === templateId}
                    onClick={() =>
                      navigate({
                        templateId: node.id,
                        layer: node.layer,
                        block: node.block,
                      })
                    }
                    isMissing={node.isMissing}
                  />
                  {replacedNode &&
                    replacesPathFrom &&
                    replacesPathTo &&
                    isShowingReplacements && (
                      <ExpandedReplacementPath
                        from={replacesPathFrom}
                        to={replacesPathTo}
                        isActive={false}
                      />
                    )}
                  {!!node.children.length && (
                    <>
                      <Polygon
                        sides={3}
                        className="fill-transparent stroke-inheritance stroke-1"
                        size={INHERITANCE_ARROW_RADIUS}
                        rotate={90}
                        center={{
                          x: node.position.x + node.boundingBox.width / 2,
                          y:
                            node.position.y +
                            node.boundingBox.height +
                            INHERITANCE_ARROW_RADIUS,
                        }}
                      />
                      {node.children.map((child) => (
                        <InheritancePathHorizontal
                          key={child.text}
                          parent={node}
                          child={child}
                        />
                      ))}
                    </>
                  )}
                </Fragment>
              )
            })}
          </Group>
        </svg>
      </div>
    </div>
  )
}
